import dayjs from 'dayjs';
import _ from 'underscore';

import { useGet } from 'hooks/use-get';
import { useForm } from 'react-hook-form';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Divider, MobileStepper } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';

import FlexBox from 'components/flex-box';
import DataGrid from 'components/data-grid';
import TextField from 'components/text-field';
import NumberField from 'components/number-field';
import Autocomplete from 'components/autocomplete';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';

import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { getNursery, postStocking } from 'views/production-systems/api/productionSystems';

import { Pond, PondStatus } from 'views/production-systems/types/pond.type';
import { Stocking } from 'views/production-systems/types/stocking.type';
import { FormValues, StockingData } from 'views/production-systems/types/stocking-form.type';
import { CreateStockingInput } from 'views/production-systems/features/farm-overview/types/create-stocking-input.type';

import { isValidDecimal, isValidInteger, isValidMeanWeight } from 'utils/validate-numbers';
import { getCycleName } from 'views/production-systems/utils/get-cycle-name';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';

import { usePost } from 'hooks/use-post';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import ListSelectedPonds from 'views/production-systems/features/farm-overview/components/list-selected-ponds';
import RegistrationHeader from 'views/production-systems/components/registration-header';
import DuplicateRegistrationWarning, {
  REGISTRATION_EXISTS_QUERY_KEY,
} from 'views/production-systems/features/farm-overview/components/duplicate-registration-warning';
import { RegistrationActionType } from 'views/production-systems/features/farm-overview/types/registration-action-type.enum';

type Props = {
  ponds: Pond[];
  handleClose?: () => void;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.stocking';

type Row = {
  pondId: string;
  pondName: string;
  previousCycleId: number;
  cycleId: number;
  cycleName: string;
  startMw: number;
  targetMw: number;
  startQty: number;
  targetQty: number;
  targetEndDate: number;
  survivalRate: number;
};

const MAX_START_QUANTITY = 10000000;
const SURVIVAL_RATE_PERCENTAGE = 150;

const AddStocking: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  //#region STATES
  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;
  const [confirm, setConfirm] = useState<boolean>(false);

  const [selectedPonds, setSelectedPonds] = useState<Pond[]>(ponds);
  //#endregion STATES

  //#region FORMS

  const defaultStockingValues = {
    deliveryTrackingCode: '',
    genetics: '',
    larvaeCost: '',
    maturation: 0,
    nauplius: 0,
    laboratory: 0,
  };

  const defaultFormValues = {
    ...defaultStockingValues,
    maturation: null,
    nauplius: null,
    laboratory: null,
  };

  const [stockingData, setStockingData] = useState<StockingData>(defaultStockingValues);

  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
    shouldUnregister: false,
    mode: 'all',
  });

  const {
    control,
    formState: { errors },
  } = form;

  const initialRows = useMemo(() => {
    return selectedPonds.map((pond) => ({
      pondId: pond.pondId,
      previousCycleId: pond.cycleId,
      cycleName: pond.pondStatus === PondStatus.active ? pond.cycleName : getCycleName(pond.pondName, registrationDate),
      startMw: 0,
      targetMw: 0,
      startQty: 0,
      targetQty: 0,
      targetEndDate: 0,
      survivalRate: 0,
      pondName: pond.pondName,
      cycleId: pond.cycleId,
    }));
  }, [selectedPonds]);

  const [rows, setRows] = useState(initialRows || []);
  // endregion FORMS

  const { data: getNurseries } = useGet(['getNursery'], () => getNursery(globalSiteId), {
    enabled: !!globalSiteId,
  });

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow, targetQty: Math.round(newRow.startQty * (newRow.survivalRate / 100)) };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const columns: GridColDef[] = [
    {
      field: 'pondName',
      flex: 2,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.destinationPond` }),
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              width: '100%',
            }}>
            {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={row.pondName}>
              {row.pondName}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'cycleName',
      flex: 1,
      editable: false,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.cycleName` }),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'startQty',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.startQuantity` }),
      type: 'number',
      editable: true,
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const { value } = params.props;
        const hasError = !isValidInteger(value) || Number(value) > MAX_START_QUANTITY;
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-startQty`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'startMw',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.startMeanWeight` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: true,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const { value } = params.props;
        const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-startMw`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'biomass',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.biomass` }),
      sortable: false,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      valueGetter: (value, row) => calculateBiomass(row.startMw, row.startQty),
    },
    {
      field: 'targetMw',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.targetMeanWeight` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: true,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const { value } = params.props;
        const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-targetMw`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'survivalRate',
      headerName: `${intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.survivalRate` })} (%)`,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: true,
      flex: 1,
      valueFormatter: (value: number) => `${value}%`,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const { value } = params.props;
        const hasError = !isValidDecimal(params.props.value) || !(Number(params.props.value) <= SURVIVAL_RATE_PERCENTAGE);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-survivalRate`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'targetEndDate',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.targetEndDate` }),
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      type: 'number',
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = !isValidInteger(params.props.value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-targetEndDate`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'targetQty',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.targetQuantity` }),
      flex: 1,
      editable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value, row) => Math.round(row.startQty * (row.survivalRate / 100)),
    },
  ] as GridColDef[];

  const summaryValues: SummaryItem[] = useMemo(() => {
    let rate = rows.map((s) => s.survivalRate).reduce((prev, curr) => Number(prev) + Number(curr), 0) / rows.length;

    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.startMw, curr.startQty);

        return {
          survivalRate: 0,
          biomass: prev.biomass + biomass,
          startMw: 0,
          targetMw: Number(prev.targetMw) + Number(curr.targetMw),
          startQty: Number(prev.startQty) + Number(curr.startQty),
          targetQty: Number(prev.targetQty) + Number(curr.targetQty),
        };
      },
      {
        survivalRate: rate,
        targetQty: 0,
        biomass: 0,
        startQty: 0,
        targetMw: 0,
        startMw: 0,
      }
    );

    newSummary.startMw = calculateMeanWeight(newSummary.biomass, newSummary.startQty);
    newSummary.targetMw = newSummary.targetMw / rows.length;
    newSummary.survivalRate = Number(rate.toFixed(2));

    return [
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.startQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.startMw,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.biomass,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.targetMw,
        type: 'number',
        hidden: false,
      },
      {
        value: `${newSummary.survivalRate}%`,
        type: 'percent',
        hidden: false,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.targetQty,
        type: 'number',
        hidden: false,
      },
    ];
  }, [rows]);

  //#region POST DATA
  const createStocking = usePost<Stocking, { globalSiteId: string; data: CreateStockingInput }>(
    (request) => postStocking(request.globalSiteId, request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  //#region HANDLERS
  const handleSubmit = () => {
    createStocking.mutate({
      globalSiteId,
      data: {
        ...stockingData,
        laboratory: Number(stockingData.laboratory),
        maturation: Number(stockingData?.maturation),
        nauplius: Number(stockingData?.nauplius),
        larvaeCost: Number(stockingData?.larvaeCost),
        date: registrationDate.format('YYYY-MM-DD'),
        cycles: rows.map((row) => ({
          ...row,
          targetEndDate: registrationDate.add(row.targetEndDate, 'days').format('YYYY-MM-DD'),
        })),
      } as CreateStockingInput,
    });
  };

  const formatSubmitData = (data: FormValues) => {
    return {
      ...data,
      laboratory: Number(data.laboratory?.id),
      maturation: Number(data.maturation?.id ?? 0),
      nauplius: Number(data.nauplius?.id ?? 0),
    };
  };

  const handleNext = (data: FormValues) => {
    setStockingData(formatSubmitData(data));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return (
        !isValidDecimal(row.survivalRate) ||
        !(Number(row.survivalRate) <= SURVIVAL_RATE_PERCENTAGE) ||
        !isValidInteger(row.targetEndDate) ||
        !isValidDecimal(row.targetMw) ||
        !isValidDecimal(row.startMw) ||
        !isValidInteger(row.startQty) ||
        Number(row.startQty) > MAX_START_QUANTITY
      );
    });
  }, [rows]);

  const onKeyDown = (e: any) => e.stopPropagation();
  //#endregion HANDLERS

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  return lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <FlexBox>
          <ListSelectedPonds selectedPonds={selectedPonds} setSelectedPonds={setSelectedPonds} />
          <Divider />
          <Box gap={3} display={'flex'}>
            <TextField
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.deliveredTrackingCode` })}
              dark
              controller={{
                control: control,
                name: 'deliveryTrackingCode',
              }}
              stopPropagation
              onKeyDown={onKeyDown}
            />
            <TextField
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.genetics` })}
              dark
              controller={{
                control: control,
                name: 'genetics',
              }}
              stopPropagation
              onKeyDown={onKeyDown}
            />
            <NumberField
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.larvaCost` })}
              dark
              controller={{
                control: control,
                name: 'larvaeCost',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    isValidDecimal: (v: number) => isValidDecimal(v),
                  },
                },
              }}
              stopPropagation
              onKeyDown={onKeyDown}
            />
            <Autocomplete
              dark
              fullWidth
              controller={{
                name: 'laboratory',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                },
              }}
              helperText={errors.laboratory?.message}
              multiple={false}
              disableCloseOnSelect={false}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.laboratoryPl` })}
              getOptionLabel={(option) => String((option as { name: string })?.name)}
              options={getNurseries?.laboratories.map((item) => ({ id: item.id, name: item.name })) || []}
            />
            <Autocomplete
              dark
              fullWidth
              controller={{
                name: 'maturation',
                control: control,
              }}
              multiple={false}
              disableCloseOnSelect={false}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.maturation` })}
              getOptionLabel={(option) => String((option as { name: string })?.name)}
              options={getNurseries?.maturations.map((item) => ({ id: item.id, name: item.name })) || []}
            />
            <Autocomplete
              dark
              fullWidth
              controller={{
                name: 'nauplius',
                control: control,
              }}
              multiple={false}
              disableCloseOnSelect={false}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.nauplius` })}
              getOptionLabel={(option) => String((option as { name: string })?.name)}
              options={getNurseries?.nauplii.map((item) => ({ id: item.id, name: item.name })) || []}
            />
          </Box>
        </FlexBox>
      )}
      {activeStep === 1 && (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.Stocking} ponds={selectedPonds} />
          <RegistrationHeader />
          <DataGrid
            rows={rows}
            apiRef={apiRef}
            columns={columns}
            onCellEditStop={handleEditing}
            getRowId={(row) => row.pondId}
            processRowUpdate={processRowUpdate}
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${intl.formatMessage({
                    id: 'productionSystems.registrationLog.registrationTypes.stocking',
                  })}-${dayjs().format('DD/MM/YYYY')}`,
                },
              },
              footer: {
                summaryValues: summaryValues,
              } as DataGridSummaryProps,
            }}
            showToolbar
            slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
          />
        </Box>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={createStocking.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              startIcon={createStocking.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={form.handleSubmit((data) => handleNext(data))} type="submit" disabled={activeStep === 1}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

export default React.memo(AddStocking);
